'use client';

import type { IconifyIcon } from '@ahd/shared/types/misc/iconify-icon';
import type { IconProps } from '@iconify/react';
import type { TypographyProps } from '@mui/material/Typography';

import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import { Icon } from '@iconify/react';
import { mergeClasses } from 'minimal-shared/utils';

import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';

import { iconifyClasses } from './classes';

// ----------------------------------------------------------------------

export type IconifyProps = Omit<React.ComponentProps<typeof IconRoot>, 'icon'> &
    Omit<IconProps, 'icon'> & {
        color?: TypographyProps['color'];
        fullSize?: boolean;
        icon: IconifyIcon;
    };

export function Iconify(props: IconifyProps) {
    const { className, color, fullSize, height, icon, sx, width = 20, ...other } = props;

    const scaledWidth = `calc(${typeof width === 'number' ? `${width}px` : width} * ${!fullSize && typeof icon !== 'string' && icon?.type === 'iconify' && !isUndefinedNullOrEmpty(icon?.scale) ? icon?.scale : 1})`;
    const scaledHeight = !isUndefinedNullOrEmpty(height)
        ? `calc(${typeof height === 'number' ? `${height}px` : height} * ${!fullSize && typeof icon !== 'string' && icon?.type === 'iconify' && !isUndefinedNullOrEmpty(icon?.scale) ? icon?.scale : 1})`
        : undefined;

    const commonIconProps: Omit<React.ComponentProps<typeof IconRoot>, 'icon'> = {
        className: mergeClasses([iconifyClasses.root, className]),
        ssr: true,
        sx: [
            {
                color,
                width: scaledWidth,
                height: scaledHeight || scaledWidth,
                fontSize: scaledHeight || scaledWidth,
                flexShrink: 0,
                display: 'inline-flex',
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ],
        ...other,
    };

    if (typeof icon === 'string' || icon?.type === 'iconify') {
        return <IconRoot {...commonIconProps} icon={icon} />;
    } else {
        <Badge
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            badgeContent={
                <IconRoot
                    {...commonIconProps}
                    icon={icon?.badge}
                    sx={[
                        ...(Array.isArray(commonIconProps?.sx) ? commonIconProps?.sx || [] : [commonIconProps?.sx]),
                        {
                            borderRadius: 100,
                            width: '0.75em',
                            height: '0.75em',
                        },
                    ]}
                />
            }
            sx={{
                fontSize: scaledHeight || scaledWidth,
            }}
        >
            <IconRoot {...commonIconProps} icon={icon?.main} />
        </Badge>;
    }
}

// ----------------------------------------------------------------------

const IconRoot = styled(Icon)``;
